import { useState, useEffect, useCallback, useMemo } from "react";
import {
  getLlmTracking, runLlmTracking, runLlmQueryNow, addLlmTrackedQuery, updateLlmTrackedQuery, deleteLlmTrackedQuery,
  gaStatus, gaLlmTraffic, dfsBalance,
  type LlmPlatform, type LlmFrequency, type LlmTrackedQuery, type LlmSnapshot,
  type LlmTrackingData, type LlmRunError,
  type GaTrafficRow, type GaTrafficTotal, type DfsBalance,
} from "../lib/authClient";
import { siteKey } from "../lib/siteData";
import { showAlert, showConfirm } from "../lib/dialog";
import { confirmDeletePopover } from "../lib/confirmPopover";

// LLM Mention Tracking — three DataForSEO-powered sections:
//   1. Brand mentions across LLM platforms (latest run).
//   2. Tracked queries run on a recurring cadence → brand-citation counts over time.
//   3. Common citation sources each platform uses.
// Read-mostly + query CRUD; the recurring runs are driven server-side by the scheduler.

interface Props { siteUrl?: string; siteBrand?: string; }

const PLATFORMS: { key: LlmPlatform; label: string }[] = [
  { key: "google_ai_overview", label: "Google AI Overview" },
  { key: "gemini", label: "Gemini" },
  { key: "chatgpt", label: "ChatGPT" },
  { key: "perplexity", label: "Perplexity" },
  { key: "claude", label: "Claude" },
];
const platformLabel = (k: string) => PLATFORMS.find(p => p.key === k)?.label ?? k;
const PLATFORM_COLOR: Record<LlmPlatform, string> = {
  google_ai_overview: "#ea580c", gemini: "#2563eb", chatgpt: "#16a34a", perplexity: "#7c3aed", claude: "#db2777",
};
const FREQ_LABEL: Record<LlmFrequency, string> = { daily: "Daily", weekly: "Weekly", monthly: "Monthly" };
// Warn when the DataForSEO balance (USD) falls below this — tracking stops when credits hit zero.
const LOW_BALANCE_USD = 10;
const usd = (n: number) => n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });

interface HoverPt { leftPct: number; topPct: number; platform: LlmPlatform; date: string; count: number; color: string; }

// Inline SVG trend chart: one line per platform, a dot at every crawl, with a custom
// light-themed hover tooltip (date + count). Last 6 months. Fills the section width; no
// charting dependency (matches the app's SVG-based viz).
function MentionTrendChart({ snapshots, platforms, unit = "mention", windowDays }: { snapshots: LlmSnapshot[]; platforms: LlmPlatform[]; unit?: string; windowDays?: number }) {
  const [hover, setHover] = useState<HoverPt | null>(null);
  // Which LLMs are hidden from the graph (toggled via the legend checkboxes). Default: all shown.
  const [hidden, setHidden] = useState<Set<LlmPlatform>>(new Set());
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const gridStroke = isDark ? "rgba(255,255,255,.09)" : "#eef2f6";
  const axisFill   = isDark ? "#93a4c0" : "#94a3b8";
  if (!snapshots.length) return <div style={{ fontSize: 11.5, color: isDark ? "#7c8aa0" : "#94a3b8" }}>No data yet — run this query to start the trend.</div>;
  const visiblePlatforms = platforms.filter(p => !hidden.has(p));

  const W = 1000, H = 150, padL = 30, padR = 14, padT = 12, padB = 22;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const now = Date.now();
  // A positive windowDays bounds the visible range (used by the GA traffic chart); when
  // omitted the chart shows the entire recorded history (the per-query mention trend).
  const windowStart = windowDays && windowDays > 0 ? now - windowDays * 86400000 : -Infinity;
  const series = visiblePlatforms.map(p => ({
    platform: p,
    pts: snapshots
      .filter(s => s.platform === p)
      .map(s => ({ t: Date.parse(s.capturedAt), m: s.mentions, iso: s.capturedAt }))
      .filter(pt => !isNaN(pt.t) && pt.t >= windowStart - 86400000)
      .sort((a, b) => a.t - b.t),
  })).filter(s => s.pts.length);
  const maxM = Math.max(1, ...series.flatMap(s => s.pts.map(p => p.m)));
  // Fit the x-axis to the actual data span so each day gets real horizontal room
  // (rather than squeezing everything into a fixed 6-month window, which packs
  // daily points a few pixels apart). Keep a minimum span so one or two points
  // don't stretch oddly, and never start earlier than the 6-month cap.
  const allT = series.flatMap(s => s.pts.map(p => p.t));
  const MIN_SPAN = 10 * 86400000; // ~10 days minimum visible span
  const dayPad = 0.5 * 86400000;  // half-day of padding on each edge
  const dataStart = allT.length ? Math.min(...allT) : now - MIN_SPAN;
  const start = Math.max(windowStart, Math.min(dataStart - dayPad, now - MIN_SPAN));
  const domEnd = now + dayPad;    // small right pad so the latest point isn't glued to the edge
  const x = (t: number) => padL + Math.max(0, Math.min(1, (t - start) / (domEnd - start))) * plotW;
  const y = (m: number) => padT + plotH - (m / maxM) * plotH;
  // Platforms captured in the same run share a timestamp, and identical mention
  // counts (usually 0) put their dots at the same spot — so one platform's line
  // would run straight through another platform's dot, looking like it connects
  // ChatGPT to Perplexity. Dodge each platform a few units horizontally so every
  // line/dot stays visibly its own; the shift is imperceptible on the date scale.
  const DODGE = 4; // viewBox units between platforms at the same timestamp
  const xOffFor = (si: number) => (si - (series.length - 1) / 2) * DODGE;
  const yTicks = maxM <= 2 ? [0, maxM] : [0, Math.round(maxM / 2), maxM];
  const xTicks = Array.from({ length: 5 }, (_, i) => start + (i / 4) * (domEnd - start));
  const fmtTick = (t: number) => new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const fmtFull = (iso: string) => { const d = new Date(iso); return isNaN(d.getTime()) ? iso.slice(0, 10) : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };
  const above = (hover?.topPct ?? 100) > 30; // flip below the dot when it's near the top (avoids clipping)
  // Clamp the tooltip horizontally so it never runs past the chart edges (the card clips overflow).
  const hx = !hover ? "-50%" : hover.leftPct <= 14 ? "0%" : hover.leftPct >= 86 ? "-100%" : "-50%";

  return (
    <div>
      <div style={{ position: "relative" }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", maxWidth: "100%" }} role="img" aria-label="Brand mentions over time by platform">
          {yTicks.map(v => (
            <g key={v}>
              <line x1={padL} y1={y(v)} x2={W - padR} y2={y(v)} stroke={gridStroke} strokeWidth={1} />
              <text x={padL - 5} y={y(v) + 3} textAnchor="end" fontSize={6.4} fill={axisFill} fontFamily="'Inter','Mulish',sans-serif">{v}</text>
            </g>
          ))}
          {xTicks.map((t, i) => (
            <text key={i} x={x(t)} y={H - 7} textAnchor={i === 0 ? "start" : i === 4 ? "end" : "middle"} fontSize={6.4} fill={axisFill} fontFamily="'Inter','Mulish',sans-serif">{fmtTick(t)}</text>
          ))}
          {series.map((s, si) => {
            const xOff = xOffFor(si);
            return (
            <g key={s.platform}>
              {s.pts.length > 1 && <polyline fill="none" stroke={PLATFORM_COLOR[s.platform]} strokeWidth={1.5} vectorEffect="non-scaling-stroke" points={s.pts.map(p => `${x(p.t) + xOff},${y(p.m)}`).join(" ")} />}
              {s.pts.map((p, i) => (
                <g key={i}>
                  <circle cx={x(p.t) + xOff} cy={y(p.m)} r={2.6} fill={PLATFORM_COLOR[s.platform]} />
                  <circle
                    cx={x(p.t) + xOff} cy={y(p.m)} r={9} fill="transparent" style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHover({ leftPct: ((x(p.t) + xOff) / W) * 100, topPct: (y(p.m) / H) * 100, platform: s.platform, date: fmtFull(p.iso), count: p.m, color: PLATFORM_COLOR[s.platform] })}
                    onMouseLeave={() => setHover(null)}
                  />
                </g>
              ))}
            </g>
            );
          })}
        </svg>
        {hover && (
          <div style={{
            position: "absolute", left: `${hover.leftPct}%`, top: `${hover.topPct}%`,
            transform: `translate(${hx}, ${above ? "calc(-100% - 9px)" : "9px"})`,
            background: isDark ? "#0f1b30" : "#ffffff", color: isDark ? "#dbe3ee" : "#334155", border: `1px solid ${isDark ? "rgba(255,255,255,.14)" : "#e2e8f0"}`, borderRadius: 6,
            boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.5)" : "0 4px 14px rgba(15,23,42,.14)", padding: "5px 9px", fontSize: 11, lineHeight: 1.35,
            fontFamily: "'Inter','Mulish',sans-serif", whiteSpace: "nowrap", pointerEvents: "none", zIndex: 5,
          }}>
            <span style={{ color: hover.color, fontWeight: 700 }}>{platformLabel(hover.platform)}</span>
            <span style={{ color: isDark ? "#93a4c0" : "#64748b" }}> · {hover.date}</span>
            <div style={{ color: isDark ? "#e6edf6" : "#0f172a", fontWeight: 700, marginTop: 1 }}>{hover.count} {unit}{hover.count === 1 ? "" : "s"}</div>
          </div>
        )}
      </div>
      {/* Legend doubles as per-LLM show/hide checkboxes for the graph above. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 8 }}>
        {platforms.map(p => {
          const on = !hidden.has(p);
          return (
            <label key={p} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, color: on ? (isDark ? "#aeb9cc" : "#475569") : (isDark ? "#5a6b85" : "#a3adba"), cursor: "pointer", userSelect: "none" }} title={on ? `Hide ${platformLabel(p)}` : `Show ${platformLabel(p)}`}>
              <input
                type="checkbox"
                checked={on}
                onChange={() => setHidden(prev => { const n = new Set(prev); n.has(p) ? n.delete(p) : n.add(p); return n; })}
                style={{ width: 13, height: 13, accentColor: PLATFORM_COLOR[p], cursor: "pointer", flex: "0 0 auto" }}
              />
              <span style={{ width: 10, height: 3, borderRadius: 2, background: on ? PLATFORM_COLOR[p] : (isDark ? "#475569" : "#cbd5e1"), display: "inline-block" }} />{platformLabel(p)}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function fmtDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? (iso || "").slice(0, 10) : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ── In-flight crawls are tracked at MODULE level so they survive the component unmounting when the
// user switches to another tab. The fetch keeps running server-side either way (results are written
// to the DB), but without this the UI would lose the spinner and the pending result, and the button
// would re-enable — inviting a duplicate crawl that spends credits. On return we re-attach to any
// still-running crawl to restore the spinner and apply its result. ──
type LlmRunResult = LlmTrackingData & { errors: LlmRunError[] };
const llmAllRuns = new Map<string, Promise<LlmRunResult>>();   // siteKey → "Check LLM Mentions" (all queries)
const llmQueryRuns = new Map<string, Promise<LlmRunResult>>(); // query id → single-query "Run now"

// Status line for a completed all-queries run.
function allRunStatus(d: LlmRunResult): { tone: "info" | "err"; text: string } {
  if (d.errors.length) {
    const summary = d.errors.slice(0, 3).map(e => `${e.platform === "all" ? "" : platformLabel(e.platform) + ": "}${e.error}`).join(" · ");
    return { tone: "err", text: `${d.snapshots.length} snapshot(s) recorded. Some platforms returned errors — ${summary}${d.errors.length > 3 ? " …" : ""}` };
  }
  return { tone: "info", text: `Done — ${d.snapshots.length} snapshot${d.snapshots.length === 1 ? "" : "s"} recorded.` };
}
// Status line for a completed single-query run.
function queryRunStatus(d: LlmRunResult, queryText: string): { tone: "info" | "err"; text: string } {
  if (d.errors.length) {
    const summary = d.errors.slice(0, 3).map(e => `${e.platform === "all" ? "" : platformLabel(e.platform) + ": "}${e.error}`).join(" · ");
    return { tone: "err", text: `Ran “${queryText}” — some platforms returned errors: ${summary}${d.errors.length > 3 ? " …" : ""}` };
  }
  return { tone: "info", text: `Done — “${queryText}” recorded a fresh snapshot.` };
}

export function LlmMentionTracking(props: Props) {
  const key = props.siteUrl ? siteKey(props.siteUrl) : "";

  const [queries, setQueries] = useState<LlmTrackedQuery[]>([]);
  const [snapshots, setSnapshots] = useState<LlmSnapshot[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState<{ tone: "info" | "err"; text: string } | null>(null);

  // Add-query form.
  const [brandInput, setBrandInput] = useState(props.siteBrand ?? "");
  const [newQuery, setNewQuery] = useState("");
  const [selPlatforms, setSelPlatforms] = useState<LlmPlatform[]>(PLATFORMS.map(p => p.key));
  const [freq, setFreq] = useState<LlmFrequency>("weekly");
  const [expanded, setExpanded] = useState<Set<string>>(new Set()); // query ids whose graph is open
  const toggleGraph = useCallback((id: string) => setExpanded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }), []);
  const [runningQ, setRunningQ] = useState<Set<string>>(new Set()); // query ids running on-demand
  const [freqEdit, setFreqEdit] = useState<{ id: string; value: LlmFrequency } | null>(null); // inline frequency editor

  // DataForSEO account balance (account-level, not per-site) — drives the low-balance / access
  // warning at the top of the section so credits running out never silently stops tracking again.
  const [balance, setBalance] = useState<DfsBalance | null>(null);

  // ── Section 4: LLM Traffic to Website (Google Analytics) ──
  const [gaConf, setGaConf] = useState<{ configured: boolean; connected: boolean; property: string } | null>(null);
  const [gaDays, setGaDays] = useState(30);
  const [gaLoading, setGaLoading] = useState(false);
  const [gaTraffic, setGaTraffic] = useState<{ daily: GaTrafficRow[]; totals: GaTrafficTotal[] } | null>(null);
  const [gaErr, setGaErr] = useState<string | null>(null);

  useEffect(() => { if (props.siteBrand && !brandInput) setBrandInput(props.siteBrand); }, [props.siteBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  // The database is the single source of truth for this tab — brand-mention and
  // citation history is stored server-side per website, so it's identical on every
  // device and can't be faked by a stale browser cache. We deliberately do NOT persist
  // to localStorage: doing so once made the tab look populated on one computer while the
  // real (DB) data was empty/lost elsewhere.
  const load = useCallback(async () => {
    if (!key) return;
    setLoading(true);
    try {
      const d = await getLlmTracking(key);
      setQueries(d.queries); setSnapshots(d.snapshots); setLoaded(true);
    } catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Could not load tracking data." }); }
    finally { setLoading(false); }
  }, [key]);

  // Clear the previous site's data immediately when the active website changes, so nothing
  // from another site lingers on screen while the new site's data loads from the server.
  useEffect(() => { setQueries([]); setSnapshots([]); setLoaded(false); setStatus(null); }, [key]);
  useEffect(() => { void load(); }, [load]);

  // Balance is account-level, so fetch once on mount (and refresh after a run, since a run spends
  // credits). Failures are non-fatal — the banner simply doesn't render.
  const loadBalance = useCallback(async () => {
    try { setBalance(await dfsBalance()); } catch { /* leave prior/none — never block the tab */ }
  }, []);
  useEffect(() => { void loadBalance(); }, [loadBalance]);

  // GA connection status for the active site (drives the LLM Traffic section).
  useEffect(() => {
    let alive = true;
    setGaConf(null); setGaTraffic(null); setGaErr(null);
    if (!key) return;
    (async () => {
      try { const s = await gaStatus(key); if (alive) setGaConf(s); }
      catch { if (alive) setGaConf({ configured: false, connected: false, property: "" }); }
    })();
    return () => { alive = false; };
  }, [key]);

  const loadGaTraffic = useCallback(async () => {
    if (!key) return;
    setGaLoading(true); setGaErr(null);
    try {
      const end = new Date();
      const start = new Date(end.getTime() - (gaDays - 1) * 86400000);
      const fmt = (d: Date) => d.toISOString().slice(0, 10);
      setGaTraffic(await gaLlmTraffic(key, fmt(start), fmt(end)));
    } catch (e) { setGaErr(e instanceof Error ? e.message : "Could not load traffic."); }
    finally { setGaLoading(false); }
  }, [key, gaDays]);

  const runNow = useCallback(async () => {
    if (running || !key) return;
    if (!queries.some(q => q.enabled)) { void showAlert("Add at least one tracked query first, then Run Now.", { title: "No Queries" }); return; }
    setRunning(true);
    setStatus({ tone: "info", text: "Querying the AI platforms…" });
    // Reuse an in-flight run for this site (e.g. one started before a tab switch) rather than
    // starting a second; keep it at module level so it survives the component unmounting.
    let p = llmAllRuns.get(key);
    if (!p) {
      p = runLlmTracking(key);
      llmAllRuns.set(key, p);
      void p.catch(() => {}).finally(() => llmAllRuns.delete(key));
    }
    try {
      const d = await p;
      setQueries(d.queries); setSnapshots(d.snapshots);
      setStatus(allRunStatus(d));
    } catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Run failed." }); }
    finally { setRunning(false); }
  }, [running, key, queries]);

  const addQuery = useCallback(async () => {
    const q = newQuery.trim();
    const brand = (brandInput.trim() || props.siteBrand || "").trim();
    if (!key) { void showAlert("Select a website first (top of the page).", { title: "No Website" }); return; }
    if (!brand) { void showAlert("Enter the brand name to track.", { title: "Brand Required" }); return; }
    if (!q) { void showAlert("Enter a search query to track.", { title: "Query Required" }); return; }
    if (!selPlatforms.length) { void showAlert("Select at least one platform.", { title: "No Platforms" }); return; }
    try {
      const created = await addLlmTrackedQuery({ siteKey: key, brand, query: q, platforms: selPlatforms, frequency: freq });
      setQueries(prev => [...prev, created]);
      setNewQuery("");
    } catch (e) { void showAlert(e instanceof Error ? e.message : "Could not add query.", { title: "Error" }); }
  }, [newQuery, brandInput, props.siteBrand, key, selPlatforms, freq]);

  const removeQuery = useCallback(async (qid: string) => {
    try {
      await deleteLlmTrackedQuery(qid);
      setQueries(prev => prev.filter(q => q.id !== qid));
      setSnapshots(prev => prev.filter(s => s.queryId !== qid));
    } catch (e) { void showAlert(e instanceof Error ? e.message : "Could not delete query.", { title: "Error" }); }
  }, []);

  const toggleEnabled = useCallback(async (q: LlmTrackedQuery) => {
    const enabled = !q.enabled;
    setQueries(prev => prev.map(x => x.id === q.id ? { ...x, enabled } : x));
    try { await updateLlmTrackedQuery(q.id, { enabled }); } catch { void load(); }
  }, [load]);

  // Save an inline frequency change.
  const saveFreq = useCallback(async (id: string) => {
    const fe = freqEdit;
    if (!fe || fe.id !== id) return;
    setQueries(prev => prev.map(x => x.id === id ? { ...x, frequency: fe.value } : x));
    setFreqEdit(null);
    try { await updateLlmTrackedQuery(id, { frequency: fe.value }); } catch { void load(); }
  }, [freqEdit, load]);

  // Run a single tracked query on demand.
  const runOne = useCallback(async (q: LlmTrackedQuery) => {
    if (runningQ.has(q.id)) return;
    setRunningQ(prev => new Set(prev).add(q.id));
    setStatus({ tone: "info", text: `Running “${q.query}”…` });
    // Reuse / persist the in-flight single-query run at module level so it survives a tab switch.
    let p = llmQueryRuns.get(q.id);
    if (!p) {
      p = runLlmQueryNow(q.id);
      llmQueryRuns.set(q.id, p);
      void p.catch(() => {}).finally(() => llmQueryRuns.delete(q.id));
    }
    try {
      const d = await p;
      setQueries(d.queries); setSnapshots(d.snapshots);
      setStatus(queryRunStatus(d, q.query));
    } catch (e) { setStatus({ tone: "err", text: e instanceof Error ? e.message : "Run failed." }); }
    finally { setRunningQ(prev => { const n = new Set(prev); n.delete(q.id); return n; }); }
  }, [runningQ]);

  // Re-attach to a "Check LLM Mentions" run still in flight from before a tab switch: restore the
  // spinner and apply its result when it finishes (the crawl itself never stopped — see llmAllRuns).
  useEffect(() => {
    if (!key) return;
    const p = llmAllRuns.get(key);
    if (!p) return;
    let alive = true;
    setRunning(true);
    setStatus({ tone: "info", text: "Querying the AI platforms…" });
    p.then(d => { if (alive) { setQueries(d.queries); setSnapshots(d.snapshots); setStatus(allRunStatus(d)); } })
      .catch(e => { if (alive) setStatus({ tone: "err", text: e instanceof Error ? e.message : "Run failed." }); })
      .finally(() => { if (alive) setRunning(false); });
    return () => { alive = false; };
  }, [key]);

  // Re-attach to any single-query "Run now" crawls still in flight after returning to this tab.
  useEffect(() => {
    const attach = queries.filter(q => llmQueryRuns.has(q.id) && !runningQ.has(q.id));
    if (!attach.length) return;
    let alive = true;
    setRunningQ(prev => { const n = new Set(prev); attach.forEach(q => n.add(q.id)); return n; });
    for (const q of attach) {
      const p = llmQueryRuns.get(q.id);
      if (!p) continue;
      p.then(d => { if (alive) { setQueries(d.queries); setSnapshots(d.snapshots); setStatus(queryRunStatus(d, q.query)); } })
        .catch(e => { if (alive) setStatus({ tone: "err", text: e instanceof Error ? e.message : "Run failed." }); })
        .finally(() => { if (alive) setRunningQ(prev => { const n = new Set(prev); n.delete(q.id); return n; }); });
    }
    return () => { alive = false; };
  }, [queries]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Aggregations ──
  const latestByQueryPlatform = useMemo(() => {
    const m = new Map<string, LlmSnapshot>();
    for (const s of snapshots) { const k = `${s.queryId}|${s.platform}`; const cur = m.get(k); if (!cur || s.capturedAt > cur.capturedAt) m.set(k, s); }
    return m;
  }, [snapshots]);
  const mentionsByPlatform = useMemo(() => {
    const t = new Map<string, number>();
    for (const s of latestByQueryPlatform.values()) t.set(s.platform, (t.get(s.platform) ?? 0) + s.mentions);
    return t;
  }, [latestByQueryPlatform]);
  const citationsByPlatform = useMemo(() => {
    const per = new Map<string, Map<string, number>>();
    for (const s of snapshots) { let dm = per.get(s.platform); if (!dm) { dm = new Map(); per.set(s.platform, dm); } for (const d of s.citedDomains) dm.set(d, (dm.get(d) ?? 0) + 1); }
    return per;
  }, [snapshots]);
  const snapshotsByQuery = useMemo(() => {
    const m = new Map<string, LlmSnapshot[]>();
    for (const s of snapshots) { const arr = m.get(s.queryId) ?? []; arr.push(s); m.set(s.queryId, arr); }
    return m;
  }, [snapshots]);

  const notReady = !key;
  // Theme-aware palette (reuses the dark values used across the other tabs) so the inline-styled
  // panels/cards/text match the app in dark mode. Reads the theme on render; App re-renders the tree
  // on theme toggle so this stays in sync.
  const isDark = typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark";
  const cardBg     = isDark ? "#16223a" : "#fff";
  const cardBorder = isDark ? "rgba(255,255,255,.14)" : "#e2e8f0";
  const hdrBg      = isDark ? "#26375a" : "#def5e7";
  const hdrBorder  = isDark ? "rgba(255,255,255,.10)" : "#c7ecd6";
  const boxBg      = isDark ? "rgba(255,255,255,.04)" : "#f8fafc";
  const boxBorder  = isDark ? "rgba(255,255,255,.12)" : "#e2e8f0";
  const ink        = isDark ? "#e6edf6" : "#0f172a";
  const ink2       = isDark ? "#93a4c0" : "#64748b";
  const inkMuted   = isDark ? "#7c8aa0" : "#94a3b8";
  const inkLabel   = isDark ? "#aeb9cc" : "#475569";
  const inkMid     = isDark ? "#cbd5e1" : "#334155";
  const link       = isDark ? "#7dd3fc" : "#2563eb";
  const red        = isDark ? "#f87171" : "#dc2626";
  const green      = isDark ? "#86efac" : "#166534";
  const bannerAmberT: React.CSSProperties = { ...bannerBase, background: isDark ? "rgba(245,158,11,.12)" : "#fffbeb", borderColor: isDark ? "rgba(245,158,11,.4)" : "#fcd34d", color: isDark ? "#fcd34d" : "#92400e" };
  const bannerRedT: React.CSSProperties = { ...bannerBase, background: isDark ? "rgba(239,68,68,.12)" : "#fef2f2", borderColor: isDark ? "rgba(239,68,68,.4)" : "#fecaca", color: isDark ? "#fca5a5" : "#991b1b" };
  const brandTitle: React.CSSProperties = { fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontSize: 20, fontWeight: 500, textTransform: "uppercase", color: "#f97316", letterSpacing: ".7px" };
  const help: React.CSSProperties = { fontFamily: "'Inter','Mulish',sans-serif", fontSize: 12, color: ink2, marginTop: 4, lineHeight: 1.5, maxWidth: 760 };

  return (
    <div className="content-drawer" style={{ marginTop: 8 }}>
      <div style={{ padding: "0 9px", marginBottom: 16 }}>
        <div className="cd-hd-brand" style={brandTitle}>LLM Mention Tracking</div>
        <div className="cd-help" style={help}>
          Tracks how often your brand is mentioned across AI platforms (Google AI Overview, Gemini, ChatGPT, Perplexity, and Claude).
          Add the search queries you want monitored and a cadence — the app runs them on that schedule to build a citation trend over time,
          and surfaces the sources each platform cites so you know where to build presence. Grok is not yet covered.
          <br /><br />
          <strong>Each crawl runs a live web search on every model</strong> — the same way a real user&rsquo;s query would — so the results
          reflect what the AI actually returns and cites today, not an answer recalled from training data or a guess at whether your
          website <em>could</em> appear as a citation. Models used: Google&rsquo;s live AI Overview, ChatGPT (GPT-4.1 mini),
          Claude (Sonnet), Gemini (2.5 Pro), and Perplexity (Sonar).
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "flex-start", gap: 12, marginTop: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 7 }}>
            <button
              onClick={async () => {
                if (running || notReady) return;
                const ok = await showConfirm(
                  "This will do a search for all queries entered below to see current mentions. It is useful if entering queries below for the first time or if you do not set an automated updated frequency per query. If queries are already set to update at a selected frequency, it is not necessary to use credits to check all query mentions at once now. Queries being tracked below can also be updated individually by clicking the play button. Click Continue to recrawl all queries now.",
                  { title: "Check LLM Mentions", confirmLabel: "Continue", cancelLabel: "Cancel", tone: "info" },
                );
                if (ok) void runNow();
              }}
              disabled={running || notReady}
              onMouseEnter={e => { if (!(running || notReady)) { e.currentTarget.style.filter = "brightness(1.08)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(234,88,12,0.35)"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
              onMouseLeave={e => { e.currentTarget.style.filter = ""; e.currentTarget.style.boxShadow = ""; e.currentTarget.style.transform = ""; }}
              style={{ padding: "10px 18px", border: "none", borderRadius: 0, fontFamily: "'Inter','Mulish',sans-serif", fontSize: 13, fontWeight: 700, color: "#fff", cursor: running || notReady ? "default" : "pointer", background: running || notReady ? "#fdba74" : "linear-gradient(135deg,#ea580c,#f97316)", whiteSpace: "nowrap", transition: "filter 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease" }}
            >
              {running ? "Running…" : "Check LLM Mentions"}
            </button>
            {/* While running, the status sits directly under the button. */}
            {running && status && (
              <div style={{ fontSize: 12.5, color: status.tone === "err" ? red : ink2, display: "flex", alignItems: "center", gap: 7 }}>
                <span className="lsg-spinner" />{status.text}
              </div>
            )}
            {notReady && <span style={{ fontSize: 12.5, fontWeight: 600, color: link }}>Select a website to use this tool.</span>}
          </div>
        </div>
      </div>

      {/* Non-running status (Done / errors) stays in its own row below. */}
      {status && !running && (
        <div style={{ padding: "0 9px", marginBottom: 16, fontSize: 12.5, color: status.tone === "err" ? red : ink2, display: "flex", alignItems: "center", gap: 7 }}>
          {loading && <span className="lsg-spinner" />}{status.text}
        </div>
      )}

      {/* DataForSEO balance banner: red on an access/auth error, amber when low, a quiet line when healthy. */}
      {balance?.configured && balance.ok === false && (
        <div style={bannerRedT}>
          <strong>DataForSEO account issue:</strong> {balance.error || "account unreachable"}. AI mention tracking can&rsquo;t run until this is resolved in your DataForSEO account.
        </div>
      )}
      {balance?.configured && balance.ok && balance.balanceUsd != null && balance.balanceUsd < LOW_BALANCE_USD && (
        <div style={bannerAmberT}>
          <strong>Low DataForSEO balance: {usd(balance.balanceUsd)}.</strong> AI mention tracking will stop when credits run out — top up at{" "}
          <a href="https://app.dataforseo.com/" target="_blank" rel="noopener noreferrer" style={{ color: "inherit", fontWeight: 700 }}>dataforseo.com</a>.
        </div>
      )}
      {balance?.configured && balance.ok && balance.balanceUsd != null && balance.balanceUsd >= LOW_BALANCE_USD && (
        <div style={{ padding: "0 9px", marginBottom: 16, fontSize: 11.5, color: inkMuted }}>
          DataForSEO balance: {usd(balance.balanceUsd)}
        </div>
      )}

      {/* ── Section 1: Brand Mentions by Platform ── */}
      <div style={{ padding: "0 9px", marginBottom: 24 }}>
        <div className="cd-section">
          <div className="cd-section-hd"><span className="cd-hd-brand">Brand Mentions by Platform</span></div>
          <p className="cd-help">Total brand mentions in the latest run of your tracked queries, per platform.</p>
          {!loaded ? (
            <p className="cd-help">{loading ? "Loading…" : "Add queries below and click Run Now to see mention counts."}</p>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {PLATFORMS.map(p => (
                <div key={p.key} style={{ flex: "1 1 150px", minWidth: 140, border: `1px solid ${cardBorder}`, borderRadius: 10, background: cardBg, padding: "12px 14px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: inkLabel, textTransform: "uppercase", letterSpacing: .4 }}>{p.label}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: "#ea580c", lineHeight: 1.1, marginTop: 6 }}>{(mentionsByPlatform.get(p.key) ?? 0).toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: inkMuted, marginTop: 2 }}>mentions</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Section 2: Tracked Queries Over Time ── */}
      <div style={{ padding: "0 9px", marginBottom: 24 }}>
        <div className="cd-section">
          <div className="cd-section-hd"><span className="cd-hd-brand">Tracked Queries Over Time</span></div>
          <p className="cd-help">Queries run on the chosen cadence. Mentions accumulate into a trend below.</p>

          {/* Add-query form */}
          <div style={{ border: `1px solid ${boxBorder}`, borderRadius: 8, background: boxBg, padding: "12px 14px", marginBottom: 14 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
              <label style={{ flex: "1 1 180px", minWidth: 160 }}>
                <span style={{ display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: ink2, marginBottom: 3 }}>Brand</span>
                <input value={brandInput} onChange={e => setBrandInput(e.target.value)} placeholder="Your brand name" style={inp} />
              </label>
              <label style={{ flex: "2 1 260px", minWidth: 220 }}>
                <span style={{ display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: ink2, marginBottom: 3 }}>Search query to track</span>
                <input value={newQuery} onChange={e => setNewQuery(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); void addQuery(); } }} placeholder="Enter a search query" style={inp} />
              </label>
              <label style={{ flex: "0 0 auto" }}>
                <span style={{ display: "block", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: .5, color: ink2, marginBottom: 3 }}>Frequency</span>
                <select value={freq} onChange={e => setFreq(e.target.value as LlmFrequency)} style={{ ...inp, cursor: "pointer" }}>
                  <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                </select>
              </label>
              <button onClick={() => void addQuery()} style={{ padding: "8px 16px", border: "none", borderRadius: 6, background: "#16a34a", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}>+ Add</button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 10 }}>
              {PLATFORMS.map(p => (
                <label key={p.key} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: inkMid, cursor: "pointer" }}>
                  <input type="checkbox" checked={selPlatforms.includes(p.key)} onChange={e => setSelPlatforms(prev => e.target.checked ? [...prev, p.key] : prev.filter(x => x !== p.key))} />
                  {p.label}
                </label>
              ))}
            </div>
          </div>

          {/* Query list + per-query trend */}
          {queries.length === 0 ? (
            <p className="cd-help">{loading && !loaded ? "Loading…" : "No tracked queries yet — add one above."}</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {queries.map(q => {
                const qSnaps = (snapshotsByQuery.get(q.id) ?? []);
                const open = expanded.has(q.id);
                return (
                  <div key={q.id} style={{ border: `1px solid ${cardBorder}`, borderRadius: 8, background: cardBg, overflow: "hidden", opacity: q.enabled ? 1 : 0.55 }}>
                    <div
                      className="llm-card-hd"
                      onClick={() => toggleGraph(q.id)}
                      title="Click to view the brand mentions graph"
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, background: hdrBg, borderBottom: `1px solid ${hdrBorder}`, padding: "9px 13px", flexWrap: "wrap" }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <span style={{ fontSize: 13, color: ink }}>
                          <span style={{ color: ink2, fontWeight: 600 }}>Brand:</span> <span style={{ fontWeight: 500 }}>{q.brand}</span>
                          <span style={{ color: inkMuted, margin: "0 7px" }}>|</span>
                          <span style={{ color: ink2, fontWeight: 600 }}>Search Query:</span> <span style={{ fontWeight: 500 }}>{q.query}</span>
                        </span>
                        <div style={{ fontSize: 10.5, color: green, marginTop: 3, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }} onClick={e => e.stopPropagation()}>
                          {freqEdit && freqEdit.id === q.id ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: isDark ? "#1a2740" : "#fff", border: `1px solid ${cardBorder}`, borderRadius: 6, padding: "2px 6px" }}>
                              <span style={{ fontWeight: 600 }}>Frequency:</span>
                              <select value={freqEdit.value} onChange={e => setFreqEdit({ id: q.id, value: e.target.value as LlmFrequency })} style={{ fontSize: 10.5, border: "1px solid #cbd5e1", borderRadius: 4, padding: "1px 4px", color: "#0f172a", cursor: "pointer" }}>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                              </select>
                              <button onClick={() => void saveFreq(q.id)} style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: "#16a34a", border: "none", borderRadius: 4, padding: "2px 9px", cursor: "pointer" }}>Save</button>
                            </span>
                          ) : (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                              <span style={{ fontWeight: 600 }}>Frequency:</span> {FREQ_LABEL[q.frequency]}
                              <button onClick={e => { e.stopPropagation(); setFreqEdit({ id: q.id, value: q.frequency }); }} title="Edit frequency" style={{ background: "none", border: "none", cursor: "pointer", color: green, padding: 0, lineHeight: 0, marginLeft: 2 }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                              </button>
                            </span>
                          )}
                          <span style={{ color: inkMuted }}>·</span>
                          <span>{q.platforms.map(platformLabel).join(", ")}</span>
                          <span style={{ color: inkMuted }}>·</span>
                          <span>last run {q.lastRunAt ? fmtDate(q.lastRunAt) : "never"}</span>
                          <a href="#" onClick={e => { e.preventDefault(); e.stopPropagation(); toggleGraph(q.id); }} style={{ fontSize: 11, fontWeight: 600, color: link, textDecoration: "none", whiteSpace: "nowrap", marginLeft: 4 }}>
                            {open ? "Hide graph" : "View brand mentions graph"}
                          </a>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                        <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: inkMid, cursor: "pointer" }}>
                          <input type="checkbox" checked={q.enabled} onChange={() => void toggleEnabled(q)} /> Active
                        </label>
                        <button onClick={() => void runOne(q)} disabled={runningQ.has(q.id)} title="Run this query now" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: runningQ.has(q.id) ? "default" : "pointer", color: "#ea580c", padding: 2, lineHeight: 0 }}>
                          {runningQ.has(q.id) ? <span className="lsg-spinner" /> : <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>}
                        </button>
                        <button onClick={e => { e.stopPropagation(); confirmDeletePopover(e.currentTarget, () => void removeQuery(q.id), "Remove this tracked query and its history?"); }} title="Remove query" style={{ background: "none", border: "none", cursor: "pointer", color: red, padding: 2, lineHeight: 0 }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                        </button>
                      </div>
                    </div>
                    {open && (
                      <div style={{ padding: "12px 14px" }}>
                        <MentionTrendChart snapshots={qSnaps} platforms={q.platforms} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Section 3: Common Citation Sources ── */}
      <div style={{ padding: "0 9px", marginBottom: 8 }}>
        <div className="cd-section">
          <div className="cd-section-hd"><span className="cd-hd-brand">Common Citation Sources</span></div>
          <p className="cd-help">Domains each platform cites most across your tracked queries — the sites to earn placement on so you get pulled into AI answers. (Google AI Overview, Perplexity, and ChatGPT expose citations most reliably.)</p>
          {!loaded ? (
            <p className="cd-help">{loading ? "Loading…" : "Run your queries to collect citation data."}</p>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {PLATFORMS.map(p => {
                const dm = citationsByPlatform.get(p.key);
                const top = dm ? Array.from(dm.entries()).sort((a, b) => b[1] - a[1]).slice(0, 12) : [];
                return (
                  <div key={p.key} className="lmt-cite-box" style={{ flex: "1 1 165px", minWidth: 158, border: `1px solid ${cardBorder}`, borderRadius: 8, background: cardBg, overflow: "hidden" }}>
                    <div style={{ background: hdrBg, borderBottom: `1px solid ${hdrBorder}`, padding: "7px 12px", fontSize: 12, fontWeight: 700, color: ink }}>{p.label}</div>
                    {top.length === 0 ? (
                      <div style={{ padding: "10px 12px", fontSize: 11.5, color: inkMuted }}>No citation data yet.</div>
                    ) : (
                      <table className="cd-history" style={{ margin: 0 }}>
                        <thead><tr><th>Domain</th><th style={{ textAlign: "center" }}>Times cited</th></tr></thead>
                        <tbody>{top.map(([domain, count]) => (
                          <tr key={domain}><td><a href={`https://${domain}`} target="_blank" rel="noopener noreferrer">{domain}</a></td><td style={{ textAlign: "center" }}>{count}</td></tr>
                        ))}</tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Section 4: LLM Traffic to Website (Google Analytics) ── */}
      <div style={{ padding: "0 9px", marginBottom: 8 }}>
        <div className="cd-section">
          <div className="cd-section-hd"><span className="cd-hd-brand">LLM Traffic to Website</span></div>
          <p className="cd-help">
            Referral sessions to your site from AI chat platforms (ChatGPT, Perplexity, Gemini, Claude), via Google Analytics.
            Google AI Overviews can&rsquo;t be separated (they count as Google organic), and app clicks that drop the referrer
            land in Direct — so this is a floor, not a total.
          </p>

          {!key ? (
            <p className="cd-help">Select a website to view its AI-platform traffic.</p>
          ) : gaConf === null ? (
            <p className="cd-help">Checking Google Analytics connection…</p>
          ) : !gaConf.configured ? (
            <p className="cd-help">Google sign-in isn&rsquo;t configured on this server yet.</p>
          ) : !gaConf.connected || !gaConf.property ? (
            <p className="cd-help">
              Connect this website to Google Analytics in <strong>⚙ Settings</strong> (next to Google Search Console) and pick a
              GA4 property to see AI-platform referral traffic here.
            </p>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                <select
                  value={gaDays}
                  onChange={e => setGaDays(Number(e.target.value))}
                  disabled={gaLoading}
                  style={{ ...inp, width: "auto", padding: "7px 10px" }}
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={180}>Last 6 months</option>
                  <option value={365}>Last 12 months</option>
                  <option value={730}>Last 2 years</option>
                </select>
                <button
                  onClick={() => void loadGaTraffic()}
                  disabled={gaLoading}
                  style={{ padding: "8px 16px", border: "none", borderRadius: 0, fontFamily: "'Inter','Mulish',sans-serif", fontSize: 12.5, fontWeight: 700, color: "#fff", cursor: gaLoading ? "default" : "pointer", background: gaLoading ? "#93c5a3" : "linear-gradient(135deg,#15803d,#16a34a)" }}
                >
                  {gaLoading ? "Loading…" : "Load traffic"}
                </button>
                {gaLoading && <span className="lsg-spinner" />}
              </div>

              {gaErr && <p className="cd-help" style={{ color: red }}>{gaErr}</p>}

              {gaTraffic && !gaErr && (
                gaTraffic.daily.length === 0 ? (
                  <p className="cd-help">No AI-platform referral sessions in this period.</p>
                ) : (() => {
                  // Reuse the trend chart: synthesize snapshots (sessions in place of mentions).
                  const gaSnaps: LlmSnapshot[] = gaTraffic.daily.map((r, i) => ({
                    id: `ga-${i}`, queryId: "ga", platform: r.platform, capturedAt: r.date, mentions: r.sessions, citedDomains: [],
                  }));
                  const gaPlatforms = Array.from(new Set(gaTraffic.daily.map(r => r.platform))) as LlmPlatform[];
                  return (
                    <>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
                        {gaTraffic.totals.map(t => (
                          <div key={t.platform} style={{ flex: "1 1 130px", minWidth: 120, border: `1px solid ${cardBorder}`, borderRadius: 8, background: cardBg, padding: "9px 12px" }}>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: inkLabel }}>
                              <span style={{ width: 10, height: 3, borderRadius: 2, background: PLATFORM_COLOR[t.platform], display: "inline-block" }} />{platformLabel(t.platform)}
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: ink, marginTop: 2 }}>{t.sessions.toLocaleString()}</div>
                            <div style={{ fontSize: 10.5, color: inkMuted }}>sessions · {t.engagedSessions.toLocaleString()} engaged</div>
                          </div>
                        ))}
                      </div>
                      <MentionTrendChart snapshots={gaSnaps} platforms={gaPlatforms} unit="session" windowDays={gaDays} />
                    </>
                  );
                })()
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const inp: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 6,
  fontSize: 12.5, fontFamily: "'Inter','Mulish',sans-serif", color: "#0f172a", outline: "none",
};

const bannerBase: React.CSSProperties = {
  margin: "0 9px 16px", padding: "10px 14px", borderRadius: 8, border: "1px solid",
  fontSize: 12.5, lineHeight: 1.5, fontFamily: "'Inter','Mulish',sans-serif",
};
